import React, { useMemo, useState, useCallback } from 'react';
import { StyleSheet, View, Pressable } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import useResponsiveness from '@/hooks/useResponsiveness';
import { BORDER_RADIUS } from '@/constants/config';

const createStyles = (colors, isXS, isHovered, compact) => StyleSheet.create({
  container: {
    width: compact ? '100%' : isXS ? 140 : 160,
    marginRight: compact ? 0 : 12,
    marginBottom: compact ? 0 : undefined,
  },
  imageContainer: {
    width: '100%',
    aspectRatio: 1,
    borderRadius: compact ? BORDER_RADIUS.small : BORDER_RADIUS.medium,
    overflow: 'hidden',
    backgroundColor: colors.solid_color_secondary,
  },
  image: {
    width: '100%',
    height: '100%',
    transform: isHovered ? [{ scale: 1.02 }] : [{ scale: 1 }],
    // Ensure smooth scale transition on web/macOS using transition for transform property
    ...(typeof window !== 'undefined' ? {
      transitionProperty: 'transform',
      transitionDuration: '150ms',
      transitionTimingFunction: 'ease',
    } : {
      transitionDuration: '150ms',
    }),
  },
  imagePlaceholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.solid_color_tertiary,
  },
  label: {
    marginTop: compact ? 4 : 8,
    textAlign: 'center',
  },
});

const ImageStyleCard = ({ 
  style: styleData, 
  onPress,
  isLoading = false,
  compact = false,
}) => {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const [isHovered, setIsHovered] = useState(false);

  const styles = useMemo(() => createStyles(colors, isXS, isHovered, compact), [colors, isXS, isHovered, compact]);

  const handlePress = useCallback(() => {
    if (onPress && styleData) {
      onPress(styleData);
    }
  }, [onPress, styleData]);

  if (isLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.imageContainer}>
          <View style={styles.imagePlaceholder} />
        </View>
        <View style={{ height: 16, marginTop: 8, backgroundColor: colors.solid_color_tertiary, borderRadius: 4 }} />
      </View>
    );
  }

  return (
    <Pressable
      style={styles.container}
      onPress={handlePress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
    >
      <View style={styles.imageContainer}>
        {(styleData?.previewGifCdn || styleData?.previewImageCdn || styleData?.previewImage) ? (
          <Image
            source={{ uri: styleData.previewGifCdn || styleData.previewImageCdn || styleData.previewImage }}
            placeholder={styleData?.previewGifCdn ? { uri: styleData.previewImageCdn || styleData.previewImage } : colors.solid_color_tertiary}
            placeholderContentFit="cover"
            style={[styles.image, { backgroundColor: colors.solid_color_tertiary }]}
            contentFit="cover"
            cachePolicy="memory-disk"
            priority="high"
            transition={150}
          />
        ) : (
          <View style={styles.imagePlaceholder} />
        )}
      </View>
      <Text 
        size={compact ? 'xs' : 's'} 
        weight="normal" 
        color="secondary"
        style={styles.label}
        numberOfLines={compact ? 1 : 2}
      >
        {styleData?.name || ''}
      </Text>
    </Pressable>
  );
};

export default React.memo(ImageStyleCard);

